import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X, Copy, Check, AlertCircle, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
interface SummarizeButtonProps {
  content: string;
  title?: string;
  contextType?: string;
  className?: string;
  variant?: 'floating' | 'inline' | 'icon' | 'header';
}
export function SummarizeButton({
  content,
  title = 'Resumen',
  contextType = 'texto',
  className = '',
  variant = 'floating'
}: SummarizeButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const handleSummarize = async () => {
    if (!isOpen) setIsOpen(true);
    if (summary && !error) return;
    setIsLoading(true);
    setError(null);
    try {
      const cleanContent = content.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim().slice(0, 15000);
      const systemPrompt = `Sos un asistente experto en resumir información técnica. 
Tu tarea es resumir este/a ${contextType}. 
Reglas:
- Sé conciso y directo.
- Usá viñetas para los puntos clave.
- Resaltá en negrita los conceptos más importantes.
- Mantené un tono profesional.
- Usá headings (##) para separar secciones si el contenido es largo.
- Si el texto es muy corto, simplemente extraé la idea principal.`;
      const res = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: systemPrompt
          }, {
            role: 'user',
            content: `Texto a resumir:\n\n${cleanContent}`
          }]
        })
      });
      if (!res.ok) throw new Error('Error al generar el resumen');
      const data = await res.json();
      setSummary(data.choices[0].message.content);
    } catch (err: any) {
      setError(err.message || 'Ocurrió un error al generar el resumen.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleCopy = () => {
    if (!summary) return;
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleClose = () => setIsOpen(false);
  // ── Shared Result Modal (used by all variants via portal) ──
  const resultModal = createPortal(<AnimatePresence data-id="element-2826">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6" onClick={handleClose} data-id="element-2827">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-2828" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.92,
        y: 24
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.92,
        y: 24
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 350
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl shadow-black/60" data-id="element-2829">
            {/* Gradient top accent */}
            <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500 shrink-0" data-id="element-2830" />

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-slate-900 border-b border-slate-800 shrink-0" data-id="element-2831">
              <div className="flex items-center gap-3 min-w-0" data-id="element-2832">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-indigo-500/30 flex items-center justify-center shrink-0" data-id="element-2833">
                  <Sparkles size={18} className="text-indigo-400" data-id="element-2834" />
                </div>
                <div className="min-w-0" data-id="element-2835">
                  <h3 className="text-base font-semibold text-slate-100" data-id="element-2836">
                    Resumen IA
                  </h3>
                  <p className="text-xs text-slate-500 truncate max-w-xs" data-id="element-2837">
                    {title}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" data-id="element-2838">
                {summary && !isLoading && <button onClick={handleCopy} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" title="Copiar resumen" data-id="element-2839">
                    {copied ? <>
                        <Check size={14} className="text-emerald-400" data-id="element-2840" />
                        <span className="text-emerald-400" data-id="element-2841">Copiado</span>
                      </> : <>
                        <Copy size={14} data-id="element-2842" />
                        <span data-id="element-2843">Copiar</span>
                      </>}
                  </button>}
                <button onClick={handleClose} className="p-2 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors ml-1" data-id="element-2844">
                  <X size={16} data-id="element-2845" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto bg-slate-950" data-id="element-2846">
              {isLoading ? <div className="flex flex-col items-center justify-center py-16" data-id="element-2847">
                  <div className="relative w-14 h-14 flex items-center justify-center mb-5" data-id="element-2848">
                    <div className="absolute inset-0 rounded-full border-2 border-indigo-500/20" data-id="element-2849" />
                    <div className="absolute inset-0 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" data-id="element-2850" />
                    <Sparkles size={18} className="text-indigo-400 animate-pulse" data-id="element-2851" />
                  </div>
                  <p className="text-sm font-medium text-slate-300 animate-pulse" data-id="element-2852">
                    Analizando y resumiendo...
                  </p>
                  <p className="text-xs text-slate-600 mt-2" data-id="element-2853">
                    Esto puede tomar unos segundos
                  </p>
                </div> : error ? <div className="flex flex-col items-center justify-center py-16 text-center px-6" data-id="element-2854">
                  <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4" data-id="element-2855">
                    <AlertCircle size={24} className="text-red-400" data-id="element-2856" />
                  </div>
                  <p className="text-sm font-medium text-red-400 mb-1" data-id="element-2857">
                    No se pudo generar el resumen
                  </p>
                  <p className="text-xs text-slate-500 mb-5 max-w-sm" data-id="element-2858">
                    {error}
                  </p>
                  <button onClick={() => {
              setSummary(null);
              handleSummarize();
            }} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-sm font-medium transition-colors border border-slate-700" data-id="element-2859">
                    <RefreshCw size={14} data-id="element-2860" />
                    Reintentar
                  </button>
                </div> : summary ? <div className="p-6 sm:p-8" data-id="element-2861">
                  <div className="markdown-body" data-id="element-2862">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-2863">
                      {summary}
                    </ReactMarkdown>
                  </div>
                </div> : null}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
  // ── ICON VARIANT ──
  if (variant === 'icon') {
    return <>
        <button onClick={handleSummarize} className={`p-1.5 rounded-md transition-all duration-150 ${className}`} title="Resumir con IA" data-id="element-2864">
          <Sparkles size={13} data-id="element-2865" />
        </button>
        {resultModal}
      </>;
  }
  // ── INLINE VARIANT ──
  if (variant === 'inline') {
    return <div className={`mt-4 ${className}`} data-id="element-2866">
        {!isOpen ? <button onClick={handleSummarize} className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border border-dashed border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/10 text-indigo-400 text-sm font-medium transition-all hover:border-indigo-500/50 group" data-id="element-2867">
            <Sparkles size={16} className="group-hover:animate-pulse" data-id="element-2868" />
            Generar resumen con IA
          </button> : <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="rounded-xl border border-slate-700 bg-slate-900 overflow-hidden" data-id="element-2869">
            {/* Gradient accent */}
            <div className="h-0.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" data-id="element-2870" />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800" data-id="element-2871">
              <div className="flex items-center gap-2 text-indigo-400 font-semibold text-sm" data-id="element-2872">
                <Sparkles size={15} data-id="element-2873" />
                Resumen IA
              </div>
              <div className="flex items-center gap-1" data-id="element-2874">
                {summary && !isLoading && <button onClick={handleCopy} className="p-1.5 text-slate-500 hover:text-indigo-400 rounded-md transition-colors" title="Copiar" data-id="element-2875">
                    {copied ? <Check size={14} className="text-emerald-400" data-id="element-2876" /> : <Copy size={14} data-id="element-2877" />}
                  </button>}
                <button onClick={handleClose} className="p-1.5 text-slate-500 hover:text-slate-300 rounded-md transition-colors" title="Cerrar" data-id="element-2878">
                  <X size={14} data-id="element-2879" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4" data-id="element-2880">
              {isLoading ? <div className="flex items-center gap-3 text-indigo-400 py-6 justify-center" data-id="element-2881">
                  <div className="relative w-8 h-8 flex items-center justify-center" data-id="element-2882">
                    <div className="absolute inset-0 rounded-full border-2 border-indigo-500/20" data-id="element-2883" />
                    <div className="absolute inset-0 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" data-id="element-2884" />
                    <Sparkles size={12} className="animate-pulse" data-id="element-2885" />
                  </div>
                  <span className="text-sm text-slate-400 animate-pulse" data-id="element-2886">
                    Analizando contenido...
                  </span>
                </div> : error ? <div className="flex flex-col items-center py-6 text-center" data-id="element-2887">
                  <AlertCircle size={20} className="text-red-400 mb-2" data-id="element-2888" />
                  <p className="text-sm text-red-400 mb-3" data-id="element-2889">{error}</p>
                  <button onClick={() => {
              setSummary(null);
              handleSummarize();
            }} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-colors" data-id="element-2890">
                    <RefreshCw size={12} data-id="element-2891" /> Reintentar
                  </button>
                </div> : summary ? <div className="markdown-body text-sm" data-id="element-2892">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-2893">
                    {summary}
                  </ReactMarkdown>
                </div> : null}
            </div>
          </motion.div>}
      </div>;
  }
  // ── HEADER VARIANT (For panel headers) ──
  if (variant === 'header') {
    return <>
        <button onClick={handleSummarize} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-indigo-500/15 to-violet-500/15 text-indigo-400 border border-indigo-500/25 hover:from-indigo-500/25 hover:to-violet-500/25 hover:border-indigo-500/40 hover:text-indigo-300 transition-all ${className}`} title="Resumir con IA" data-id="element-2894">
          <Sparkles size={13} data-id="element-2895" />
          <span data-id="element-2896">Resumir</span>
        </button>
        {resultModal}
      </>;
  }
  // ── FLOATING VARIANT (default) ──
  return <>
      <div className={`sticky bottom-5 z-40 flex justify-end pointer-events-none pr-2 ${className}`} data-id="element-2897">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={handleSummarize} className="pointer-events-auto flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-lg shadow-indigo-500/25 border border-indigo-400/20 hover:shadow-indigo-500/40 hover:shadow-xl transition-all" data-id="element-2898">
          <Sparkles size={18} data-id="element-2899" />
          <span data-id="element-2900">Resumir con IA</span>
        </motion.button>
      </div>
      {resultModal}
    </>;
}